import { defineStore } from 'pinia'
import {
  AuditType,
  fieldDefaultMap,
  type AuditBooleanField,
  type FullAudit,
  type FullAuditData,
  type NewAuditRequest,
} from '~/constants/auditTypes'
import { FormStatus } from '~/constants/formTypes'
import debounce from 'lodash.debounce'
import uniq from 'lodash.uniq'
import auditApi, { type AuditListResponse } from '~/apis/auditApi'
import { getRandomName } from '~/utils/auditUtils'
import { defaultFlagMap, getFlagInfo, isFlagKey } from '~/constants/flagConsts'
import { check } from 'prettier'
import type { ProcessStage } from '~/constants/processTypes'
import type { FlagKey } from '~/constants/flagTypes'
import type { HiringIssue } from '~/constants/hiringIssueTypes'

const isDevelopment = (): boolean => {
  if (typeof window !== 'undefined') {
    return (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    )
  }
  return process.env.NODE_ENV === 'development'
}

const generateMockAudits = (count: number): FullAudit[] => {
  const statuses = [
    FormStatus.Complete,
    FormStatus.InProgress,
    FormStatus.Incomplete,
  ]
  const mockAudits: FullAudit[] = []

  const auditNames = [
    'Engineering Team Audit',
    'Product Development Review',
    'DevOps Hiring Analysis',
    'Frontend Team Assessment',
    'Backend Infrastructure Audit',
    'Data Science Recruitment',
    'Mobile Team Evaluation',
    'QA Process Review',
    'Security Team Audit',
    'Platform Engineering',
    'Cloud Infrastructure',
    'Machine Learning Ops',
    'Customer Success Tech',
    'Support Engineering',
    'Integration Team',
    'API Development',
    'UX Engineering',
    'Performance Optimization',
    'Reliability Engineering',
    'Growth Engineering',
  ]

  for (let i = 0; i < count; i++) {
    const randomDate = new Date()
    randomDate.setDate(randomDate.getDate() - Math.floor(Math.random() * 365))
    const status = statuses[Math.floor(Math.random() * statuses.length)]
    const baseName = auditNames[i % auditNames.length]

    mockAudits.push({
      id: `mock-audit-${i + 1}`,
      orgId: 'mock-org',
      auditName: `${baseName} ${Math.floor(i / auditNames.length) + 1}`,
      auditType: AuditType.Full,
      status,
      archived: Math.random() > 0.8,
      createdAt: randomDate.toISOString(),
      updatedAt: randomDate.toISOString(),
      auditData: {
        biggestChallenges: [],
        processStages: [],
        issues: {},
        flags: structuredClone(defaultFlagMap),
      },
    })
  }

  return mockAudits
}

export type SelectedAudit = Partial<FullAudit> &
  Pick<FullAudit, 'orgId' | 'id' | 'auditData' | 'status'>

const fieldsToClear: (keyof FullAuditData)[] = [
  'numberOfTechnicalHiresLast12Months',
  'numberOfTechnicalHiresNext12Months',
]

const checkAndClearFields = (audit: FullAudit): FullAudit => {
  fieldsToClear.forEach((field) => {
    const value = audit.auditData[field]
    if (field && value && isNaN(Number(value))) {
      delete audit.auditData[field]
      audit.status = FormStatus.InProgress
    }
  })
  return audit
}

const checkListAndClearFields = (list: FullAudit[]): FullAudit[] => {
  return (list ?? []).map(checkAndClearFields)
}

const debouncedSave = debounce(
  async (id: string, selectedAudit: SelectedAudit): Promise<void> => {
    const saved = await auditApi.updateAudit(id, selectedAudit)
    const store = useAuditStore()
    if (
      store.selected.id === id &&
      saved?.completedAt &&
      store.selected.completedAt !== saved.completedAt
    )
      store.selected.completedAt = saved.completedAt
  },
  1000,
  { maxWait: 5000 },
)

const interviewEffortHours: Record<number, number> = {
  0: 40,
  1: 40,
  2: 40,
  3: 50,
  4: 50,
  5: 65,
  6: 65,
  7: 80,
  8: 80,
}

// 1 - 2: interviewerEffort = 40h
// 3 - 4: interviewerEffort = 50h
// 5 - 6: interviewerEffort = 65h
// 7 - 8: interviewerEffort = 80h"
const interviewEffort = (selected: SelectedAudit) => {
  const totalSteps =
    selected.auditData?.processStages?.flatMap((steps) => steps)?.length ?? 3

  return interviewEffortHours[totalSteps] ?? 80
}

const getMinMaxFromRange = (
  selected: SelectedAudit,
  field: keyof FullAuditData,
): { min: number; max: number } | null => {
  if (
    selected.id &&
    selected?.auditData?.[field] &&
    typeof selected.auditData[field] === 'string'
  ) {
    // @ts-ignore
    if (selected.auditData[field] === "I don't know") {
      // @ts-ignore
      return fieldDefaultMap[field]
    }
    const value = selected.auditData[field]
      .replaceAll('+', '')
      .replaceAll('$', '')
      .replaceAll('<', '')
      .replaceAll('+', '')
      .replaceAll('%', '')

    const valueArray = value
      .split('-')
      .map((str) => Number([...str].filter((c) => !isNaN(Number(c))).join('')))

    return {
      min: valueArray[0],
      max: valueArray[valueArray.length - 1],
    }
  } else if (
    selected.id &&
    selected.auditData[field] &&
    typeof selected.auditData[field] === 'number'
  ) {
    return {
      min: selected.auditData[field],
      max: selected.auditData[field],
    }
  } else {
    return null
  }
}

const selectedDefault = {
  id: '',
  orgId: '',
  status: FormStatus.InProgress,
  auditData: {
    biggestChallenges: [],
    processStages: [],
    issues: {},
    flags: structuredClone(defaultFlagMap),
  },
}

export const useAuditStore = defineStore('audit', {
  state: (): {
    selected: SelectedAudit
    list: FullAudit[]
  } => ({
    // @ts-ignore
    selected: structuredClone(selectedDefault),
    list: [],
  }),
  getters: {
    getIssue:
      (state) =>
      (field: HiringIssue): boolean | undefined => {
        return state.selected.auditData.issues?.[field]
      },

    getFixedTaCost: (state) => {
      const recruiterSalary = 70000
      const baseCost = 10000
      const numberOfRecruitersObj = getMinMaxFromRange(
        state.selected,
        'numberOfTechnicalRecruiters',
      ) ?? { min: 0, max: 0 }

      const { min, max } = numberOfRecruitersObj
      const numberOfRecruiters = (min + max) / 2
      let variableCost = 10000
      if (min === max && numberOfRecruiters >= 6) {
        variableCost = 50000
      } else if (numberOfRecruiters >= 4 && numberOfRecruiters <= 6) {
        variableCost = 30000
      }
      return (
        variableCost +
        baseCost * numberOfRecruiters +
        recruiterSalary * numberOfRecruiters
      )
      // 1 - 3: fixedTaCost = $10k + $10k * numberOfRecruiters + recruiterSalary * numberOfRecruiters
      // 4 - 6: fixedTaCost = $30k + $10k * numberOfRecruiters + recruiterSalary * numberOfRecruiters
      // 6+: fixedTaCost = $50k + $10k * numberOfRecruiters + recruiterSalary * numberOfRecruiters"
    },
    getNumberOfHiresLast12Months: (state): number => {
      const { min, max } = getMinMaxFromRange(
        state.selected,
        'numberOfTechnicalHiresLast12Months',
      ) ?? { min: 0, max: 0 }

      return max
    },
    getNumberOfHiresNext12Months: (state) => {
      const { min, max } = getMinMaxFromRange(
        state.selected,
        'numberOfTechnicalHiresNext12Months',
      ) ?? { min: 0, max: 0 }

      return max
    },
    getAvgCostPerHire: (state) => {
      return getMinMaxFromRange(state.selected, 'costToHireOneTechnicalRole')
    },

    getTimeToHireMultiplier: (state) => {
      const minMax = getMinMaxFromRange(
        state.selected,
        'timeToFillTechnicalPosition',
      )
      if (minMax) {
        const { min, max } = minMax
        if (max <= 30) {
          return 0.9
        } else if (min >= 60 && max <= 90) {
          return 1.1
        } else if (min >= 90) {
          return 1.5
        }
      }
      return 1
    },

    getTimeToHire: (state) => {
      const minMax = getMinMaxFromRange(
        state.selected,
        'timeToFillTechnicalPosition',
      )

      return minMax ?? { min: 61, max: 90 }
    },

    getInterviewEffort: (state) => {
      return interviewEffort(state.selected)
    },
    getInterviewEffortCostEstimate: (state) => {
      // Directional US-tech estimate using the same published loaded rate as Impact Model v1.
      const hourlyMultiplier = 150

      // If not in interview effort, assume greater than 8
      return hourlyMultiplier * interviewEffort(state.selected)
    },

    getOfferAcceptanceRate: (state) => {
      const { min, max } = getMinMaxFromRange(
        state.selected,
        'offerAcceptanceRate',
      ) ?? { min: 60, max: 60 }
      if (min === max && max >= 80) {
        return 81
      } else {
        return min
      }
    },

    getNumberOfRecruiters: (state) => {
      return (
        getMinMaxFromRange(state.selected, 'numberOfTechnicalRecruiters')
          ?.max ?? 0
      )
    },
    getHasClosedPositionsWithoutHiring: (state) => {
      return (
        state.selected.auditData?.[
          'closedRoleWithoutHiringWithinLast12Months'
        ] ?? false
      )
    },
  },
  actions: {
    async save(flush?: boolean): Promise<void> {
      const { memberPermissions } = usePermissions()
      const p = memberPermissions.value.audits
      const ownDraft =
        p.create &&
        this.selected.createdBy === useUserStore().user?.id &&
        !this.selected.completedAt
      if (!p.view || (!p.edit && !ownDraft)) {
        debouncedSave.cancel()
        if (flush)
          throw new Error(
            'Editing permission is required to change this content.',
          )
        return
      }
      // Skip saving mock audits (development only)
      if (
        this.$state.selected.id &&
        !this.$state.selected.id.startsWith('mock-audit-')
      ) {
        await debouncedSave(this.$state.selected.id, this.$state.selected)
        if (flush) await debouncedSave.flush()
      }
    },
    async updateAuditList(): Promise<void> {
      if (!usePermissions().canViewAudits.value) {
        debouncedSave.cancel()
        this.list = []
        this.selected = structuredClone(selectedDefault)
        return
      }
      await debouncedSave.flush()
      const newAuditList: AuditListResponse = await auditApi.getAuditList()
      const clearedList = checkListAndClearFields(newAuditList.audits)
      this.$state.list = clearedList ?? []
    },
    clearSelected() {
      // @ts-ignore
      this.$state.selected = structuredClone(selectedDefault)
    },
    async createNewAudit(): Promise<any> {
      const auditName = `Audit - ${getRandomName()}`
      const newAudit: NewAuditRequest = {
        auditName,
        auditType: AuditType.Full,
        auditData: {},
      }
      const audit = await auditApi.createAudit(newAudit)
      await this.updateAuditList()
      this.$patch((state) => {
        state.selected = {
          ...structuredClone(selectedDefault),
          ...audit,
          auditData: {
            ...structuredClone(selectedDefault.auditData),
            ...audit.auditData,
          },
        }
      })
    },
    async getAudit(auditId: string): Promise<any> {
      // Handle mock audits (development only) - find from local list instead of API
      if (auditId.startsWith('mock-audit-')) {
        const mockAudit = this.$state.list.find((audit) => audit.id === auditId)
        if (mockAudit) {
          const clearedAudit = checkAndClearFields(mockAudit)
          this.$patch((state) => {
            state.selected = {
              ...structuredClone(selectedDefault),
              ...clearedAudit,
              auditData: {
                ...structuredClone(selectedDefault.auditData),
                ...clearedAudit.auditData,
              },
            }
          })
          return
        }
        throw new Error('Mock audit not found in local list')
      }

      const audit = await auditApi.getAudit(auditId)
      const clearedAudit = checkAndClearFields(audit)
      this.$patch((state) => {
        state.selected = {
          ...structuredClone(selectedDefault),
          ...clearedAudit,
          auditData: {
            ...structuredClone(selectedDefault.auditData),
            ...audit.auditData,
          },
        }
      })
    },
    updateAuditBooleanField(field: AuditBooleanField, newValue: boolean) {
      this.$state.selected.auditData[field] = newValue
    },

    updateFlags() {
      // reset flags
      this.$state.selected.auditData.flags = structuredClone(defaultFlagMap)

      const flagCheckRecursive = (dataObject: any) => {
        Object.keys(dataObject).forEach((key) => {
          const value = dataObject[key]
          if (value === undefined || value === null || value === 'flags') return
          if (typeof value === 'object') {
            flagCheckRecursive(value)
          } else if (isFlagKey(key)) {
            const flagKey = key as FlagKey
            const flagInfo = getFlagInfo(flagKey, value)
            if (flagInfo && this.$state.selected.auditData.flags) {
              const flagColorData =
                this.$state.selected.auditData.flags[flagInfo.flagColor]
              if (flagColorData) {
                const newValue = {
                  ...(flagColorData[flagKey] ?? {
                    questions: [],
                  }),
                }
                newValue.questions = uniq([...newValue.questions, flagKey])
                flagColorData[flagKey] = newValue
              }
            }
          }
        })
      }

      flagCheckRecursive(this.$state.selected.auditData)
    },

    setSelected(auditId: string) {
      const audit = this.list.find((audit) => audit.id === auditId)
      if (!audit) {
        this.$patch((state) => {
          state.selected = {
            ...structuredClone(selectedDefault),
            // @ts-ignore
            auditData: {
              ...structuredClone(selectedDefault.auditData),
            },
          }
        })
      } else {
        const clearedAudit = checkAndClearFields(audit)

        this.$patch((state) => {
          state.selected = {
            ...structuredClone(selectedDefault),
            ...clearedAudit,
            auditData: {
              ...structuredClone(selectedDefault.auditData),
              ...clearedAudit.auditData,
            },
          }
        })
      }
    },

    updateIssues(field: HiringIssue, newValue: boolean) {
      if (!this.$state.selected.auditData.issues) {
        this.$state.selected.auditData.issues = {}
      }
      ;(this.$state.selected.auditData.issues as Record<HiringIssue, boolean>)[
        field
      ] = newValue
    },

    updateStages(processStages: ProcessStage[]) {
      const field: keyof Pick<FullAuditData, 'processStages'> = 'processStages'
      this.$state.selected.auditData[field] = processStages
    },

    async updateStatus(auditStatus: FormStatus) {
      this.$state.selected.status = auditStatus
    },

    async archiveAudit(auditId: string): Promise<void> {
      // Find the audit in the list
      const auditToArchive = this.$state.list.find(
        (audit) => audit.id === auditId,
      )

      if (auditToArchive) {
        // Mark as archived
        auditToArchive.archived = true

        // Skip API call for mock audits (development only)
        if (!auditId.startsWith('mock-audit-')) {
          await auditApi.updateAudit(auditId, {
            archived: true,
          } as SelectedAudit)
          // If the archived audit is the currently selected one, update the selected audit too
          if (this.$state.selected.id === auditId) {
            await this.getAudit(auditId)
          }
        }
      }
    },
    async deleteAudit(auditId: string): Promise<void> {
      // remove from local state immediately (optimistic update)
      this.$state.list = this.$state.list.filter(
        (audit) => audit.id !== auditId,
      )

      // if deleting selected audit, clear it
      if (this.$state.selected.id === auditId) {
        this.clearSelected()
      }

      // skip API for mock audits (dev only)
      if (auditId.startsWith('mock-audit-')) {
        return
      }

      // call backend
      await auditApi.deleteAudit(auditId)
    },
  },
})

// ({
//   biggestChallenges: [],
//   offerAcceptanceRate: 0,
//   isUsingExternalResources: false,
//   timeToFillTechnicalPosition: '',
//   closedRoleWithoutHiringWithinLast12Months: false,
//   numberOfStages: 0,
//   issues: {
//     longCyclesWithDropoff: false,
//     poorStakeholderCommunication: false,
//     schedulingConflicts: false,
//     inconsistentProcess: false,
//     lackOfAutomation: false,
//   },
//   numberOfEmployeesRange: '',
//   numberOfTechnicalEmployeesRange: '',
//   numberOfTechnicalRecruiters: '',
//   numberOfTechnicalHiresLast12Months: '',
//   numberOfTechnicalHiresNext12Months: '',
//   costToHireOneTechnicalRole: '',
//   longestOpenPositionLast12Months: '',
//   salaryTarget: '',
//   isUsingPsychTest: false,
//   isRequiringReferences: false,
// }),
